import React, { useContext } from "react";
// Utils
import { CommonContext, ShowNotification } from "config/commonProvider";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { getFieldName } from "helpers/myLivestock";
import { handleErrorCheck, removeNothings } from "helpers/general";
import { useStateSpecies } from "hooks";
// Components
import FormBuilder from "components/Common/FormBuilder";
import { FieldSelect, Modal, Spacer, Text } from "components";
import { FieldNamesQuery, useFieldNamesQuery } from "generated/graphql";
import { getBUFromLocalStorage } from "helpers/storage";
import { Species } from "state/speciesSlice";

interface ConfirmMoveToFieldModalProps {
  showModal: boolean;
  onClose: () => void;
  moveAnimalToField: (
    props: {
      group?: string | null | undefined;
    },
    showNotification: ShowNotification,
    fieldsList: FieldNamesQuery["fields"],
    speciesActive?: Species,
  ) => void;
  animals: Animal[];
  error?: string;
  requesting: boolean;
  animalPassportId?: string;
}

const ConfirmMoveToFieldModal: React.FC<ConfirmMoveToFieldModalProps> = ({
  error,
  showModal,
  onClose,
  moveAnimalToField,
  animals,
  requesting,
  animalPassportId,
}) => {
  const { showNotification } = useContext(CommonContext);
  const { data: fieldsData } = useFieldNamesQuery({
    variables: {
      businessUnitId: getBUFromLocalStorage(),
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const fields = removeNothings(fieldsData?.fields || []);

  const { activeSpecies } = useStateSpecies();

  const fieldsList = fields.map((item) => {
    const label = getFieldName({ fieldName: item.name, unitName: item.location?.unitName });

    return {
      key: item.id,
      value: item.id,
      label,
    };
  });

  const assignToGroup = (props): void => moveAnimalToField(props, showNotification, fields, activeSpecies);

  return (
    <FormBuilder
      enableReinitialize
      validateOnChange={false}
      initialValues={{
        field: "",
      }}
      onSubmit={assignToGroup}
    >
      {({ values, errors, handleChange, handleSubmit }): JSX.Element => (
        <Modal
          actions={{
            primary: {
              id: ELEMENTS_IDS.MOVE_TO_FIELD_MODAL_CONFIRM_BUTTON,
              caption: "Confirm",
              disabled: requesting || values.field === "",
              requesting: requesting,
              onClick: handleSubmit,
            },
            secondary: {
              id: ELEMENTS_IDS.MOVE_TO_FIELD_MODAL_CANCEL_BUTTON,
              caption: "Cancel",
              colour: "grey",
              variant: "hollow",
              onClick: onClose,
            },
          }}
          active={showModal}
          handleClose={onClose}
          subTitle={`Please select a location to move ${animalPassportId || `these ${animals.length} animal(s)`} to`}
          title={"Move to location"}
        >
          <FieldSelect
            error={handleErrorCheck(errors.field)}
            inputProps={{
              name: "field",
              value: values.field,
              placeholder: "No location",
              options: fieldsList,
              onChange: handleChange,
              isClearable: true,
            }}
          />

          {!!error ? (
            <>
              <Spacer baselineHeight={2} />

              <Text smaller warning>
                {error}
              </Text>
            </>
          ) : null}
        </Modal>
      )}
    </FormBuilder>
  );
};

export default ConfirmMoveToFieldModal;
